<template>
  <footer class="relative overflow-hidden py-12 transition-colors duration-500"
    :class="isDark
      ? 'bg-galaxy-950 text-white'
      : 'bg-gradient-to-b from-primary-800 to-primary-900 text-white'
    "
  >
    <!-- Background effects -->
    <div class="absolute inset-0 pointer-events-none overflow-hidden">
      <div v-if="isDark" class="absolute inset-0 stars-bg opacity-20"></div>
      <div class="absolute top-0 left-1/3 w-96 h-96 rounded-full blur-3xl opacity-5 bg-nebula-purple"></div>
      <div class="absolute bottom-0 right-1/3 w-80 h-80 rounded-full blur-3xl opacity-5 bg-nebula-cyan"></div>
    </div>

    <div class="max-w-6xl mx-auto px-4 relative z-10">
      <div class="grid md:grid-cols-3 gap-8 mb-8">

        <!-- Brand -->
        <div>
          <div class="flex items-center gap-3 mb-4">
            <img src="@/assets/bugsy.png" alt="Bugsy" class="h-10 w-10 object-contain drop-shadow-md transition-transform duration-300 hover:scale-110 hover:rotate-6" />
            <span class="text-xl font-bold galaxy-text-gradient">{{ t('nav.brand') }}</span>
          </div>
          <p class="text-white/60">{{ t('footer.tagline') }}</p>
        </div>

        <!-- Contact -->
        <div>
          <h4 class="text-lg font-bold mb-4"
            :class="isDark ? 'text-nebula-cyan' : 'text-accent-300'"
          >{{ t('footer.contact') }}</h4>
          <div class="space-y-3">
            <a
              href="https://www.facebook.com/StarBugsAcademy/"
              target="_blank"
              rel="noopener noreferrer"
              class="flex items-center gap-3 text-white/70 hover:text-white transition-colors duration-200 group"
            >
              <span class="flex h-9 w-9 items-center justify-center rounded-full transition-colors duration-200"
                :class="isDark ? 'bg-white/10 group-hover:bg-[#1877F2]/30' : 'bg-white/15 group-hover:bg-[#1877F2]/40'"
              >
                <svg class="h-4 w-4 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </span>
              <span class="text-sm">Facebook</span>
            </a>
            <a
              href="https://www.instagram.com/starbugsacademyy/"
              target="_blank"
              rel="noopener noreferrer"
              class="flex items-center gap-3 text-white/70 hover:text-white transition-colors duration-200 group"
            >
              <span class="flex h-9 w-9 items-center justify-center rounded-full transition-colors duration-200"
                :class="isDark ? 'bg-white/10 group-hover:bg-[#E1306C]/30' : 'bg-white/15 group-hover:bg-[#E1306C]/40'"
              >
                <svg class="h-4 w-4 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                </svg>
              </span>
              <span class="text-sm">Instagram</span>
            </a>
          </div>
        </div>

        <!-- Follow Us -->
        <div>
          <h4 class="text-lg font-bold mb-4"
            :class="isDark ? 'text-nebula-cyan' : 'text-accent-300'"
          >{{ t('footer.social') }}</h4>
          <div class="flex gap-3">
            <a
              href="https://www.facebook.com/StarBugsAcademy/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              class="flex h-11 w-11 items-center justify-center rounded-full transition-all duration-200 hover:scale-110"
              :class="isDark
                ? 'bg-white/10 text-white/70 hover:bg-[#1877F2]/40 hover:text-white'
                : 'bg-white/15 text-white/70 hover:bg-[#1877F2]/50 hover:text-white'"
            >
              <svg class="h-5 w-5 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </a>
            <a
              href="https://www.instagram.com/starbugsacademyy/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              class="flex h-11 w-11 items-center justify-center rounded-full transition-all duration-200 hover:scale-110"
              :class="isDark
                ? 'bg-white/10 text-white/70 hover:bg-[#E1306C]/40 hover:text-white'
                : 'bg-white/15 text-white/70 hover:bg-[#E1306C]/50 hover:text-white'"
            >
              <svg class="h-5 w-5 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
              </svg>
            </a>
          </div>
        </div>

      </div>

      <div class="border-t mb-6"
        :class="isDark ? 'border-galaxy-700/30' : 'border-white/20'"
      ></div>

      <p class="text-center text-white/50 text-sm">
        {{ t('footer.copyright') }}
      </p>
    </div>
  </footer>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'

defineProps<{ isDark: boolean }>()

const { t } = useI18n()
</script>
